import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environmentCommon } from 'src/environments/environment-common';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { MailService } from '../../../service/mail/mail.service';

/**
 * The contact form (owner 2026-10-12): first and last name, email and message, sent through the mail service
 * without leaving the page, then a thank you. The nav's Contact Us popover and the Contact Us page both show it.
 * A host that wants its draft to outlive the form (the popover closes and opens again) passes its own [form].
 */
@Component( {
  selector: 'app-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: [ './contact-form.component.scss' ],
} )
export class ContactFormComponent implements OnInit {

  /** the host's form, so a draft survives the form being taken down; one of its own when none is given */
  @Input() form: FormGroup;
  /** where the message was sent from, for the analytics event */
  @Input() source = 'Contact Page';
  /** the popover's "Contact Us" head; the page has its own title over the card */
  @Input() head = true;

  @Output() sentChange = new EventEmitter<boolean>();
  /** Done on the thank you */
  @Output() done = new EventEmitter<void>();

  environmentCommon = environmentCommon;

  submitted = false;
  sending = false;
  sent = false;
  errorMessage: string | null = null;

  // in form order, so the first invalid field names itself
  private readonly fieldErrorMessages: { [ key: string ]: string } = {
    firstName: 'Please provide your first name',
    lastName: 'Please provide your last name',
    email: 'Please provide a valid email',
    message: 'Please enter your message',
  };

  constructor(
    private formBuilder: FormBuilder,
    private mailService: MailService,
    private analyticsService: AnalyticsService,
  ) {
  }

  static createForm( formBuilder: FormBuilder ): FormGroup {
    return formBuilder.group( {
      firstName: [ '', Validators.required ],
      lastName: [ '', Validators.required ],
      email: [ '', Validators.compose( [ Validators.required, Validators.email ] ) ],
      message: [ '', Validators.compose( [ Validators.required, Validators.maxLength( 2000 ) ] ) ],
    } );
  }

  ngOnInit(): void {
    if ( !this.form ) {
      this.form = ContactFormComponent.createForm( this.formBuilder );
    }
  }

  invalid( field: string ): boolean {
    return this.submitted && this.form.controls[ field ].invalid;
  }

  /** Done on the thank you: a fresh form for the next message */
  finish(): void {
    this.sent = false;
    this.submitted = false;
    this.errorMessage = null;
    this.form.reset( { firstName: '', lastName: '', email: '', message: '' } );
    this.sentChange.emit( false );
    this.done.emit();
  }

  onSubmit( event?: Event ): void {
    event?.preventDefault();
    if ( this.sending ) return;

    this.submitted = true;
    this.errorMessage = null;

    if ( this.form.invalid ) {
      const first = Object.keys( this.fieldErrorMessages ).find( key => this.form.controls[ key ].invalid );
      this.errorMessage = first ? this.fieldErrorMessages[ first ] : 'Please complete the required fields';
      return;
    }

    this.sending = true;
    const value = this.form.value;
    this.mailService.sendContactUsMail( {
      firstName: value.firstName,
      lastName: value.lastName,
      email: value.email,
      phone: '',
      website: '',
      companySize: '',
      country: '',
      message: value.message,
    } ).subscribe( {
      next: data => {
        this.sending = false;
        this.sent = !!data?.success;
        if ( this.sent ) {
          // a lead: only that it happened (no PII), and where from
          this.analyticsService.logEvent( 'Contact Submitted', { source: this.source } );
          this.sentChange.emit( true );
        } else {
          this.errorMessage = 'Could not send your message, please try again';
        }
      },
      error: () => {
        this.sending = false;
        this.errorMessage = 'Could not send your message, please try again';
      },
    } );
  }
}
